import { useAppDispatch } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { ReadingSlideData, WordBoundary } from "@ai-teach/types";
import type { MediaInput, SlideHandlers } from "../types";

/**
 * Handlers for Reading slides
 *
 * Reading slides consist of multiple passages.
 * Each passage can have its own visual (image or video) and audio.
 *
 * This hook provides media handlers scoped to a specific passage.
 */
export const useReadingHandlers = (
  slide: SlideFields | null
): SlideHandlers & {
  setVideoWordBoundaries: (wordBoundaries: WordBoundary[], passageId: string | undefined) => void;
  setPassageSubtitle: (
    text: string,
    wordBoundaries: WordBoundary[],
    passageId: string | undefined
  ) => void;
} => {
  const dispatch = useAppDispatch();

  const readingSlideData = slide?.slideData as ReadingSlideData;

  /**
   * Add or update an image for a specific passage
   *
   * Updates the `image` field on the matched passage inside `slideData.passages`
   */
  const addImage = ({ src, id, filename }: MediaInput, passageId: string | undefined) => {
    if (!slide || !passageId || !readingSlideData?.passages) {
      throw new Error("Drag image to one of the passage!");
    }

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === passageId ? { ...p, image: { src, filename, id } } : p
          ),
        },
      })
    );
  };

  /**
   * Add or update a video for a specific passage
   *
   * Updates the `video` field on the matched passage inside `slideData.passages`, and switches that
   * passage to its video visual. The existing image is left in place as the fallback older app
   * builds read.
   */
  const addVideo = ({ src, id, filename }: MediaInput, passageId: string | undefined) => {
    if (!slide || !passageId || !readingSlideData?.passages) {
      throw new Error("Drag video to one of the passage!");
    }

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === passageId
              ? { ...p, video: { src, filename, id }, mediaType: "video" as const }
              : p
          ),
        },
      })
    );
  };

  /**
   * Add or update audio for a specific passage
   *
   * Updates the `audio` field on the matched passage inside `slideData.passages`
   */
  const addAudio = ({ src, id, filename }: MediaInput, passageId: string | undefined) => {
    if (!slide || !passageId || !readingSlideData?.passages) {
      throw new Error("Drag audio to one of the passage!");
    }

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === passageId ? { ...p, audio: { src, filename, id } } : p
          ),
        },
      })
    );
  };

  /**
   * Replace a passage video's word timings, keeping its src/id/filename untouched
   */
  const setVideoWordBoundaries = (
    wordBoundaries: WordBoundary[],
    passageId: string | undefined
  ) => {
    if (!slide || !passageId || !readingSlideData?.passages) {
      throw new Error("No passage to save word timings to!");
    }

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === passageId && p.video ? { ...p, video: { ...p.video, wordBoundaries } } : p
          ),
        },
      })
    );
  };

  /**
   * Reading slides support an image, a video and audio per passage
   */
  /**
   * Writes a passage's text and its clip's word timings together, so the two can't land in
   * separate updates and leave the timings describing the previous text.
   */
  const setPassageSubtitle = (
    text: string,
    wordBoundaries: WordBoundary[],
    passageId: string | undefined
  ) => {
    if (!slide || !passageId || !readingSlideData?.passages) {
      throw new Error("No passage to save the subtitle to!");
    }

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: readingSlideData.passages.map((p) =>
            p.id === passageId && p.video
              ? {
                  ...p,
                  text,
                  spellCheckStatus: "unchecked" as const,
                  video: { ...p.video, wordBoundaries },
                }
              : p
          ),
        },
      })
    );
  };

  return {
    addImage,
    addVideo,
    addAudio,
    setVideoWordBoundaries,
    setPassageSubtitle,
  };
};
